import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/shared/Navbar';
import Sidebar from '../components/shared/Sidebar';
import { useShowViewToggle } from '../hooks/useShowViewToggle';
import { ToggleProvider } from '../contexts/ToggleContext';
import { styled } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import AddIdeaPlaceholderMessage from '../components/shared/PlaceholderMessage';
import { gql, useLazyQuery } from "@apollo/client";
import { NotificationToaster } from "../utils/NotificationToaster";

// GraphQL Query to fetch all ideas (same as in IdeaDropdown)
const GET_IDEAS = gql`
  query GetIdeas {
    ideas {
      id
      title
      createdAt
    }
  }
`;

// Query to fetch details for a specific idea, including added keywords
const GET_IDEA_DETAILS = gql`
  query GetIdeaDetails($id: ID!) {
    idea(id: $id) {
      id
      title
      keywords { name } # Changed to select subfield
    }
  }
`;

// Material-UI styled components
const Wrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: "hidden"
});

const NavbarWrapper = styled(Box)(({ showModal }) => ({
  position: 'relative',
  zIndex: showModal ? 0 : 2, // Navbar is under the Backdrop when modal is open
  width: '100%', // Navbar takes the full width
  height: '68px', // Define the height of the Navbar
}));

const LayoutWrapper = styled(Box)({
  display: 'flex',
  flex: 1, // Use flex to make Sidebar and Content side-by-side
  position: 'relative',
  zIndex: 1, // Sidebar and main content should be under the Navbar when modal is not open
});

const SidebarWrapper = styled(Box)(({ showModal }) => ({
  width: '250px', // Adjust width based on your Sidebar component
  zIndex: showModal ? 0 : 1, // Sidebar under the Navbar when modal is open
  flexShrink: 0, // Prevent sidebar from shrinking
  height: 'calc(100vh - 100px)', // Sidebar fills the remaining height after the Navbar
}));

const ContentWrapper = styled(Box)(({ step, path }) => ({
  display: 'flex',
  flex: 1,
  overflowY: step < 5 && path.includes("Explore") ? (
    'hidden'
  ) : (
    'auto'
  ),
  overflowX: step < 5 && path.includes("Explore") ? (
    'hidden'
  ) : (
    'auto'
  ),
  height: "calc(100vh - 6.3rem)",
  position: 'relative',
  padding: '0 16px',
}));

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  position: 'relative',
  zIndex: 1, // Ensure content is above the Sidebar but below the modal,
  // height: "calc(100vh + 14rem)",
  width: '100%'
});

const MainLayout = ({ children, activeView, setActiveView }) => {
  const location = useLocation();
  const step = useSelector((state) => state?.explore?.step);
  const scrollContainerRef = useRef(null);
  const dispatch = useDispatch();

  const showViewToggle = useShowViewToggle();
  const [showDashedLines, setShowDashedLines] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [initialFetchAttempted, setInitialFetchAttempted] = useState(false); // Add state to track initial fetch

  // Get ideaId and ideas list from Redux store
  const ideaId = useSelector((state) => state.explore.idea?.id);
  const ideas = useSelector((state) => state.explore.ideas); // Add this to track available ideas


  // Lazy query to fetch ideas
  const [loadIdeas, { loading: loadingIdeas }] = useLazyQuery(GET_IDEAS, {
    fetchPolicy: "network-only", // Ensure fresh data on load
    onCompleted: (data) => {
      // Only set default if NO idea is currently selected in Redux
      if (!ideaId && data?.ideas?.length > 0) {
        // Sort ideas to find the newest one
        const ideasCopy = [...data.ideas];
        ideasCopy.sort((a, b) => {
          const timeA = a.createdAt ? parseInt(a.createdAt, 10) : 0;
          const timeB = b.createdAt ? parseInt(b.createdAt, 10) : 0;
          // Handle potential NaN from parseInt
          const validTimeA = !isNaN(timeA) ? timeA : 0;
          const validTimeB = !isNaN(timeB) ? timeB : 0;
          return validTimeB - validTimeA; // Newest first
        });

        const newestIdea = ideasCopy[0]; // Get the actual newest idea object
        
        // Fetch details for the newest idea
        loadIdeaDetails({ variables: { id: newestIdea.id } });
        // Initialize Redux state with the newest idea ID
        dispatch({ type: 'IDEA_INIT', payload: { id: newestIdea.id } });

      } else if (!ideaId && data?.ideas?.length === 0) {
        dispatch({ type: 'IDEA_INIT' }); // Clear idea state when no ideas exist
      }
    },
    onError: (error) => {
      console.error("Error fetching ideas for default selection:", error);
      NotificationToaster({ message: `Failed to load initial ideas: ${error.message}`, type: 'error' });
    }
  });

  // Lazy query to fetch details of the selected default idea
  const [loadIdeaDetails, { loading: loadingDetails }] = useLazyQuery(GET_IDEA_DETAILS, {
    fetchPolicy: "network-only",
    onCompleted: (detailsData) => {
      if (detailsData?.idea) {
        // console.log("Default idea details fetched:", detailsData.idea);
        dispatch({
          type: 'SET_DEFAULT_IDEA_DETAILS', // New action type
          payload: {
            idea: { id: detailsData.idea.id, title: detailsData.idea.title }, // Basic idea info
            // Map the array of keyword objects to an array of names
            keywords: (detailsData.idea.keywords || []).map(kw => kw.name)
          }
        });
      }
    },
    onError: (error) => {
      console.error("Error fetching idea details:", error);
      NotificationToaster({ message: `Failed to load details for default idea: ${error.message}`, type: 'error' });
    }
  });

  // Effect to fetch the list of ideas on mount if none is selected
  useEffect(() => {
    // Only trigger if no ideaId, not currently loading ideas, not loading details, and initial fetch hasn't been attempted
    if (!ideaId && !loadingIdeas && !loadingDetails && !initialFetchAttempted) {
      console.log("No idea selected on mount, fetching ideas list...");
      loadIdeas();
      setInitialFetchAttempted(true); // Mark that we've attempted the initial fetch
    }
  }, [ideaId, loadIdeas, loadingIdeas, loadIdeaDetails, loadingDetails, initialFetchAttempted]); // Add initialFetchAttempted to dependencies

  // Force clear idea state if there are no ideas available
  useEffect(() => {
    // If ideas list is loaded and empty, ensure idea state is cleared
    if (Array.isArray(ideas) && ideas.length === 0) {
      console.log('MainLayout: No ideas available, clearing current idea state');
      dispatch({ type: 'IDEA_INIT' }); // Clear idea state when no ideas exist
    }
  }, [ideas, dispatch]);


  const smoothScroll = (targetPosition) => {
    const element = scrollContainerRef.current;
    const startPosition = element.scrollLeft;
    const distance = targetPosition - startPosition;
    const duration = 2000;
    let startTime = null;

    const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    const animation = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easeProgress = easeInOutQuad(progress);

      element.scrollLeft = startPosition + distance * easeProgress;

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  useEffect(() => {
    if (scrollContainerRef.current && step >= 6) {
      const newScrollPosition = step * (step === 6 ? 0 : step === 7 ? 150 : 100);
      smoothScroll(newScrollPosition);
    }

    if (scrollContainerRef.current && step === 0) {
      smoothScroll(-150);
    }
  }, [step]);

  // Function to open the modal
  const openModal = (content) => {
    setModalContent(content);
    setShowModal(true);
  };

  // Function to close the modal
  const closeModal = () => {
    setShowModal(false);
    setModalContent(null); // Clear content when closing
  };


  return (
    <ToggleProvider value={{ showDashedLines, setShowDashedLines }}>
      {/* Wrapper for the entire layout */}
      <Wrapper>
        {/* Navbar */}
        <NavbarWrapper showModal={showModal}>
          <Navbar showViewToggle={showViewToggle} onViewChange={setActiveView} activeView={activeView} />
        </NavbarWrapper>

        {/* Layout wrapper with Sidebar and Content side-by-side */}
        <LayoutWrapper>
          {/* Sidebar */}
          <SidebarWrapper showModal={showModal}>
            <Sidebar />
          </SidebarWrapper>

          {/* Main content area */}
          <ContentWrapper ref={scrollContainerRef} step={step} path={location.pathname} className={"content-wrapper"}>
            <MainContent className={"main-content-wrapper"}>
              {/* Conditionally render children or placeholder based on if idea is selected */}
              {ideaId ? (
                React.cloneElement(children, { activeView }) // Pass only necessary props
              ) : (
                <AddIdeaPlaceholderMessage />
              )}
            </MainContent>
          </ContentWrapper>
        </LayoutWrapper>

        <ToastContainer position={"top-right"} autoClose={3000} />
      </Wrapper>
    </ToggleProvider>
  );
};

export default MainLayout;
